import {
  ClipboardList,
  ListTodo,
  MessageSquareDashed,
  MessageSquareDot,
  MessagesSquare,
} from 'lucide-react'
import { createLazyComponent } from 'v2/shared/utils/lazy-utils'
import type { IRoute } from 'v2/types'
import { SidebarGroups } from 'v2/types'
import type { RFeatureFlags } from 'v2/types/api/auth'
import type { RUserProfile } from 'v2/types/api/settings'

// Lazy-loaded components
const ConversationsPage = createLazyComponent(() => import('v2/pages/conversations'))
const Outbound = createLazyComponent(() => import('v2/pages/conversations/outbound'))
const AllTasks = createLazyComponent(() => import('v2/pages/conversations/tasks/list-tasks'))
const MyTasksPage = createLazyComponent(() => import('v2/pages/conversations/tasks/my-tasks'))
const IndividualConversationPage = createLazyComponent(
  () => import('v2/pages/conversations/views/individual-conversation'),
)

export const getConversationsRoutes = (meta?: {
  featureFlags?: RFeatureFlags
  profileData?: RUserProfile
}): IRoute[] => {
  const humanSpecialistEnabled =
    meta?.profileData?.organizations?.[0]?.human_specialist_enabled ?? false
  // The human-agent workspace (conversations inbox + tasks) is unavailable to
  // orgs that aren't human-agent orgs. Gating these routes as notAllowed blocks
  // direct navigation AND drops them from the sidebar and the command palette
  // (palette excludes notAllowed routes) — org-aware cmdk.
  const isHumanAgentRestricted = !(meta?.profileData?.organizations?.[0]?.is_human_agent ?? false)
  return [
    {
      title: 'Conversations',
      path: '/conversations',
      icon: <MessagesSquare size={16} />,
      component: <ConversationsPage />,
      notAllowed: isHumanAgentRestricted,
      sidebar: {
        group: SidebarGroups.views,
        hasChevron: true,
        onClick: 'onConversationsClick',
      },
    },
    {
      title: 'Tasks',
      path: '/conversations/tasks/all',
      icon: <ListTodo size={16} />,
      component: <AllTasks />,
      sidebar: {
        group: SidebarGroups.views,
        badgeKey: 'tasksData',
      },
      hidden: !humanSpecialistEnabled,
      notAllowed: isHumanAgentRestricted,
    },

    {
      title: 'Conversation',
      path: '/v1/conversation/:conversationId',
      notAllowed: false,
      hidden: true,
      component: <IndividualConversationPage />,
    },
    // Parameterized conversation routes (top-level for proper matching)
    {
      title: 'Table view',
      path: '/conversations/:viewId',
      component: <ConversationsPage />,
      hidden: true,
    },
    {
      title: 'Conversation detail base',
      path: '/conversations/:viewId/c',
      component: <ConversationsPage />,
      hidden: true,
      notAllowed: false, // Allow all users to view conversations
    },
    {
      title: 'Conversation detail view',
      path: '/conversations/:viewId/c/:conversationId',
      component: <ConversationsPage />,
      hidden: true,
      notAllowed: false, // Allow all users to view conversations
    },
    {
      title: 'Outbound',
      path: '/conversations/new/:type',
      component: <Outbound />,
      hidden: true,
    },

    // {
    //       title: 'Exceptions',
    //       path: '/conversations/tasks/exceptions',
    //       component: <ExceptionsTasks />,
    //       hidden: true,
    //     },

    // INBOX and QUEUED sidebar items (top-level for main sidebar)
    {
      title: 'My Conversations',
      path: '/conversations/active/c',
      icon: <MessageSquareDot size={16} />,
      sidebar: {
        group: SidebarGroups.inbox,
        badgeKey: 'activeConversations',
      },
      hidden: !humanSpecialistEnabled,
      sidebarOnly: true,
      notAllowed: isHumanAgentRestricted,
    },
    {
      title: 'My Tasks',
      path: '/conversations/tasks/my-tasks',
      icon: <ClipboardList size={16} />,
      component: <MyTasksPage />,
      sidebar: {
        group: SidebarGroups.inbox,
        badgeKey: 'myTasksData',
      },
      notAllowed: isHumanAgentRestricted,
      hidden: !humanSpecialistEnabled,
    },
    {
      title: 'Unassigned Tasks',
      path: '/conversations/tasks/unassigned',
      component: <MyTasksPage />,
      hidden: true,
      notAllowed: isHumanAgentRestricted,
    },
    {
      title: 'Unassigned Conversations',
      path: '/conversations/unassigned/c',
      icon: <MessageSquareDashed size={16} />,
      sidebar: {
        group: SidebarGroups.queued,
        badgeKey: 'invitedContacts',
      },
      sidebarOnly: true,
      hidden: true,
      notAllowed: isHumanAgentRestricted,
    },
  ]
}
